/**
 * AppContext — Globaler Anwendungs-State
 * Verwaltet: Sidebar-Zustand, aktive Akte, aktiver Akten-Betrieb, Benachrichtigungen
 */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { BusinessUnit } from '@/lib/supabase';

interface ActiveAkte {
  id: string;
  aktenNr: string;
  titel: string;
  kundenName: string;
  businessUnit: BusinessUnit;
}

interface AppState {
  sidebarCollapsed: boolean;
  activeAkte: ActiveAkte | null;
  aktenBetriebFilter: BusinessUnit;
  userStatus: 'active' | 'inactive';
  unreadNotifications: number;
  forderungsmanagerOpen: boolean;
  forderungsAkteId: string | null;
}

interface AppContextType extends AppState {
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  openAkte: (akte: ActiveAkte) => void;
  closeAkte: () => void;
  setAktenBetriebFilter: (unit: BusinessUnit) => void;
  toggleUserStatus: () => void;
  openForderungsmanager: (forderungsAkteId?: string) => void;
  setForderungsmanagerOpen: (open: boolean) => void;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>({
    sidebarCollapsed: false,
    activeAkte: null,
    aktenBetriebFilter: 'gutachten',
    userStatus: 'active',
    unreadNotifications: 3,
    forderungsmanagerOpen: false,
    forderungsAkteId: null,
  });

  const toggleSidebar = useCallback(() => {
    setState(prev => ({ ...prev, sidebarCollapsed: !prev.sidebarCollapsed }));
  }, []);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setState(prev => {
      if (prev.sidebarCollapsed === collapsed) return prev;
      return { ...prev, sidebarCollapsed: collapsed };
    });
  }, []);

  const openAkte = useCallback((akte: ActiveAkte) => {
    setState(prev => {
      if (prev.activeAkte?.id === akte.id) return prev;
      return { ...prev, activeAkte: akte };
    });
  }, []);

  const closeAkte = useCallback(() => {
    setState(prev => {
      if (prev.activeAkte === null) return prev;
      return { ...prev, activeAkte: null };
    });
  }, []);

  const setAktenBetriebFilter = useCallback((unit: BusinessUnit) => {
    setState(prev => {
      if (prev.aktenBetriebFilter === unit) return prev;
      return { ...prev, aktenBetriebFilter: unit };
    });
  }, []);

  const toggleUserStatus = useCallback(() => {
    setState(prev => ({
      ...prev,
      userStatus: prev.userStatus === 'active' ? 'inactive' : 'active',
    }));
  }, []);

  const openForderungsmanager = useCallback((forderungsAkteId?: string) => {
    setState(prev => ({
      ...prev,
      forderungsmanagerOpen: true,
      forderungsAkteId: forderungsAkteId ?? null,
    }));
  }, []);

  const setForderungsmanagerOpen = useCallback((open: boolean) => {
    setState(prev => {
      if (prev.forderungsmanagerOpen === open) return prev;
      return { ...prev, forderungsmanagerOpen: open };
    });
  }, []);

  // Memoize the context value to prevent unnecessary re-renders of consumers.
  // Only re-creates the value object when state actually changes (same reference = no re-render).
  const value = useMemo<AppContextType>(() => ({
    ...state,
    toggleSidebar,
    setSidebarCollapsed,
    openAkte,
    closeAkte,
    setAktenBetriebFilter,
    toggleUserStatus,
    openForderungsmanager,
    setForderungsmanagerOpen,
  }), [state, toggleSidebar, setSidebarCollapsed, openAkte, closeAkte, setAktenBetriebFilter, toggleUserStatus, openForderungsmanager, setForderungsmanagerOpen]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
